import { createContext, useContext, useState } from 'react';
import { createTheme, ThemeProvider, alpha } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

const ThemeContext = createContext({
  isDarkMode: true,
  toggleDarkMode: () => {}
});

export const useTheme = () => useContext(ThemeContext);

/**
 * v4 design-system color tokens (repo-root DESIGN_SYSTEM.md is the spec; the v4
 * board reconciliation is #1377). One object per theme; all component color
 * decisions reference these, never a hardcoded hex.
 */
const TOKENS = {
  dark: {
    // v4 palette refresh (#1245, UI-Refresh-Spec.md §1). Headline change: surfaceRaised
    // was #0d0e11, nearly identical to surfacePage #08090b, so cards were invisible. It
    // is now #131519 with a visible #23272e hairline.
    surfacePage: '#0a0b0d',     // spec --bg (was #08090b)
    surfaceRaised: '#131519',   // spec --surface-1: cards/panels now read as a real surface
    surfaceHover: '#1a1d22',    // spec --surface-2: solid row hover/active bg + input fills
    surfaceRecessed: '#0e0f12', // spec --surface-recessed: archived block, add-row strip, group container (8.13)
    surfaceBar: '#0b0b0e',
    // v4 board addition (#1377): full-bleed hovered/active list row, between
    // surfaceRaised and surfaceHover (spec --surface-row-hover).
    surfaceRowHover: '#16191e',
    hoverOverlay: 'rgba(251, 251, 252, 0.025)', // §2.8: overlay alpha tracks off-white, not pure (intentional delta from the spec's rgba(255,...))
    textPrimary: '#e4e7eb',     // spec --text-primary (was #fafafa)
    textDefault: '#e4e4e7',
    textBody: '#d4d4d8',
    textMuted: '#c7ccd3',       // spec --text-secondary (was #a1a1aa, lifted contrast)
    textDim: '#5e646e',         // spec --text-tertiary (was #71717a)
    textFaint: '#52525b',
    borderHairline: '#23272e',  // spec --border: visible card + divider edges (was #1a1a1d)
    borderStrong: '#33373f',    // spec --border-strong: focused/emphasized borders, checkboxes
    positive: '#35c892',        // spec --positive (was #34d399)
    positiveStrong: '#5dd3bd',
    negative: '#f2647b',        // spec --negative (was #fb7185)
    warning: '#f5bc24',         // spec --warning (was #fbbf24)
    warningSecondary: '#fb923c',
    interactive: '#8b92f0',     // spec --accent (was #818cf8)
    interactiveStrong: '#3730a3',
    interactiveOn: '#c7d2fe',
    accentDeep: '#6a5cd0',      // spec --accent-deep: FAB / avatar gradient end stop
    buttonPrimaryBg: '#4f46e5',
    buttonPrimaryText: '#ffffff', // §2.8 exception: contrast text on saturated fill
    catChecking: '#38b7f0',     // spec --icon-checking (was #38bdf8)
    catCredit: '#e8843c',       // spec --icon-credit (was #fb923c)
    catSavings: '#35c892',      // spec --icon-savings (was #34d399)
    catBudgets: '#34d3b5',      // spec --icon-budget (was #5dd3bd)
    catIncome: '#facc15',
    catNeutral: '#a1a1aa',
    tintChecking: 'rgba(56, 183, 240, 0.13)',
    tintCredit: 'rgba(232, 132, 60, 0.13)',
    tintSavings: 'rgba(53, 200, 146, 0.13)',
    tintBudgets: 'rgba(52, 211, 181, 0.12)',
    tintIncome: 'rgba(250, 204, 21, 0.12)',
    tintWarning: 'rgba(245, 188, 36, 0.10)',
    tintPositive: 'rgba(53, 200, 146, 0.10)',
    tintNegative: 'rgba(242, 100, 123, 0.10)',
    tintInteractive: 'rgba(139, 146, 240, 0.10)',
    tintNeutral: 'rgba(161, 161, 170, 0.10)',
    // §7.1 bar chrome foreground (v4, #1377): the top bar + bottom nav read dimmer
    // text/label tiers than the page body. Values from tokens/colors-dark.css.
    barText: '#c7ccd3',
    barLabel: '#5e646e',
    barPositive: '#35c892',
    barNegative: '#f2647b',
    barZero: '#5e646e',
    barNavIdle: '#52525b',
    barNavActive: '#8b92f0',
    // Dark badge chip ("N Unreconciled", DEV flag), spec §3: amber text on a deep
    // amber-brown fill + border. Previously the dark chip was a plain outline
    // (warningChipBg transparent); v4 gives it the filled treatment.
    warningDeep: '#f5bc24',     // chip text = warning
    warningBorder: '#4a3a12',   // deep amber-brown border
    warningChipBg: '#1a1606',   // deep amber-brown fill
    // Search-match highlight (#1230): a translucent amber over a dark surface
    // goes muddy and the light body text loses contrast, so dark uses a
    // near-opaque amber chip with dark ink text instead of a wash.
    searchHighlightBg: 'rgba(245, 202, 70, 0.92)',
    searchHighlightText: '#1c1804',
    surfaceDanger: 'rgba(251, 113, 133, 0.03)',
    borderDanger: 'rgba(251, 113, 133, 0.18)',
    // §2.7 overlay surface (#937): a step lighter than surfaceRaised so a
    // floating dropdown visibly lifts off a card or table row underneath.
    // The #934 first pass kept overlays on the same raised color, which read
    // as 'painted on' against same-colored rows; this is the strengthened
    // step.
    surfaceOverlay: '#181a20',
    // §2.7 overlay edge (#1938, 2026-09-29): the hairline (#23272e) sat so close to
    // the overlay and the page that floating layers merged with the rows behind
    // them. A neutral step brighter (about 1.6:1 on the overlay, 1.9:1 on the
    // page), luminance only, so it holds for color-blind users.
    overlayBorder: '#3a3f48',
    // §2.7 overlay shadow (menus, popovers, autocomplete, slide-up drawers).
    // Heavier in dark because page + raised are nearly identical, so the
    // hairline border + shadow do the lift, not the elevation tint (which the
    // quiet-surface MuiPaper override removes). Strengthened in #937 with a
    // larger outer drop + contact shadow + a 1px dark contour ring. Shadow
    // alpha tracks surfacePage (rgb 10 11 13) rather than pure black, per the
    // 'no absolute black/white' rule.
    overlayShadow: '0 18px 40px rgba(10, 11, 13, 0.70), 0 6px 16px rgba(10, 11, 13, 0.55), 0 0 0 1px rgba(10, 11, 13, 0.35)',
  },
  // Cool-gray light theme (#1246, UI-Refresh-Spec.md §1b). Replaces Sage (archived
  // in docs/design/sage-light-theme.md). Calm cool-gray surfaces: page #dfe2e6 ->
  // card #eef0f3 (one step lighter) -> overlay sliver. NOT white (too bright), NOT
  // sepia. Indigo brand accent (#5b62d6). Financial signal colors are deepened for
  // the light surface so amounts stay legible. Unlike Sage, the bar chrome is LIGHT
  // (dark-on-light), tracking the calm cool-gray intent. darkMode stays binary.
  light: {
    surfacePage: '#dfe2e6',     // §1b --bg: calm cool-gray page (the "not white" base)
    surfaceRaised: '#eef0f3',   // §1b --surface-1: card, one step lighter than the page
    surfaceHover: '#d7dade',    // §1b --surface-2: solid row hover/active bg + input fills
    surfaceRecessed: '#dadde1', // §1b --surface-recessed: archived block, add-row strip, group container (8.13)
    surfaceBar: '#eef0f3',      // light utility chrome (#1246): top bar + bottom nav read dark-on-light
    // v4 board addition (#1377): full-bleed hovered/active list row (light equivalent).
    surfaceRowHover: '#e4e7ea',
    hoverOverlay: 'rgba(33, 38, 44, 0.05)',  // legacy subtle overlay; rows use --surface-2
    textPrimary: '#21262c',     // §1b --text-primary: calm slate, not pure black
    textDefault: '#262b31',
    textBody: '#3e444c',        // §1b --text-secondary
    textMuted: '#4b525b',
    textDim: '#868c95',         // §1b --text-tertiary
    textFaint: '#9aa0a8',
    borderHairline: '#c9cdd3',  // §1b --border: card + divider hairlines
    borderStrong: '#b5bac1',    // §1b --border-strong: inputs, checkboxes, emphasized borders
    positive: '#1f9d63',        // §1b --positive (deepened for light)
    positiveStrong: '#0e8d75',  // deep teal hero balance
    negative: '#d6455f',        // §1b --negative
    warning: '#9a7b12',         // §1b --warning text
    warningSecondary: '#c2571f',
    interactive: '#5b62d6',     // §1b --accent: links, active nav, focus ring
    interactiveStrong: '#4a4fbf', // deeper indigo for the strong/hover step + avatar fill
    interactiveOn: '#ffffff',   // §2.8 exception: contrast text on saturated fill
    accentDeep: '#4a4fbf',      // §1b --accent-deep: FAB / avatar gradient end stop
    buttonPrimaryBg: '#4f54cc', // saturated indigo primary action fill
    buttonPrimaryText: '#ffffff', // §2.8 exception: contrast text on saturated fill
    catChecking: '#1e8fc2',     // §1b --icon-checking
    catCredit: '#c56a28',       // §1b --icon-credit
    catSavings: '#1f9d63',      // §1b --icon-savings
    catBudgets: '#1d9e88',      // §1b --icon-budget
    catIncome: '#a8801c',       // deepened gold, distinct from amber warning
    catNeutral: '#71767e',
    tintChecking: 'rgba(30, 143, 194, 0.13)',
    tintCredit: 'rgba(197, 106, 40, 0.13)',
    tintSavings: 'rgba(31, 157, 99, 0.13)',
    tintBudgets: 'rgba(29, 158, 136, 0.12)',
    tintIncome: 'rgba(168, 128, 28, 0.13)',
    tintWarning: 'rgba(154, 123, 18, 0.12)',
    tintPositive: 'rgba(31, 157, 99, 0.12)',
    tintNegative: 'rgba(214, 69, 95, 0.12)',
    tintInteractive: 'rgba(91, 98, 214, 0.12)',  // indigo tint, tracks interactive
    tintNeutral: 'rgba(113, 118, 126, 0.12)',
    // Search-match highlight (#1230): translucent amber highlighter, text unchanged;
    // the wash over the cool-gray surface + dark body text reads fine.
    searchHighlightBg: 'rgba(250, 204, 21, 0.45)',
    searchHighlightText: 'inherit',
    surfaceDanger: 'rgba(214, 69, 95, 0.04)',
    borderDanger: 'rgba(214, 69, 95, 0.20)',
    // Light bar chrome (#1246): the top bar + bottom nav are light cool-gray, so
    // their foreground reads dark-on-light. Exposed via palette.bar.* (both modes
    // carry explicit bar* tokens as of v4 #1377).
    barText: '#21262c',          // in-bar text (workspace, user name)
    barLabel: '#4b525b',         // metric labels
    barPositive: '#1f9d63',      // positive metric values
    barNegative: '#d6455f',      // negative metric values
    barZero: '#868c95',          // zero / muted
    barNavIdle: '#868c95',       // inactive bottom-nav item
    barNavActive: '#5b62d6',     // active bottom-nav item + pill (interactive)
    // §1b warning pill: amber text on a faint amber fill + border (Unreconciled
    // chip, DEV flag). Read via theme.palette.warning.{deep,border,chipBg}.
    warningDeep: '#9a7b12',      // pill text
    warningBorder: '#d7cb93',    // pill border
    warningChipBg: '#ebe4cb',    // pill fill
    // §2.7 overlay surface (light): off-white sliver that reads brightest, a hair
    // above surface.raised so floating overlays lift off the cards. Off-white per §2.8.
    surfaceOverlay: '#f6f7f9',
    // §2.7 overlay edge (light): one neutral step darker than the hairline.
    overlayBorder: '#b5bac1',
    // §2.7 overlay shadow (light variant, kept very light per §1b so it stays calm).
    overlayShadow: '0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 6px rgba(15, 23, 42, 0.05)',
  },
};

/**
 * Non-color design tokens, theme-independent. Exposed on theme.ds so the shared
 * styled() primitives (Phase 3) read the v3 type scale, spacing, radii, and motion
 * without redefining MUI's 8px spacing base.
 */
export const DS = {
  font: {
    // v4 board scale (#1377, board wins over the #1118 +2 bump): row 14 /
    // rowCompact 13.5 row names, value 13 table + row values, valueCompact 14,
    // sectionTitle 13 card headers. hero/heroLabel/heroDetail keep their current
    // values here; v4 heroes are restyled per screen with kit-exact sizes and
    // these keys are re-pointed or retired at that step. bar stays 15 (guide
    // overlay text reads it); the S3 chrome pass sizes the top bar explicitly.
    hero: 24, display: 22, h1: 20, heroLabel: 24, metric: 16, value: 13, valueCompact: 14,
    heroDetail: 28,
    row: 14, rowCompact: 13.5, bar: 15, sectionTitle: 13, meta: 13, dim: 12,
    weightRegular: 400, weightMedium: 500,
    letterHero: '-0.01em', letterUpper: '0.04em', letterBarLabel: '0.08em',
  },
  space: { 1: 4, 2: 6, 3: 8, 4: 10, 5: 12, 6: 14, 7: 18, 8: 20, 9: 22, 10: 24, 12: 30, 14: 32 },
  // v4 (#1377): icon 5 = row icon box (board); overlay/sheet back menus,
  // popovers, and the mobile bottom sheet. (iconLg dropped at release
  // cleanup: zero consumers - the brand tile derives radius from its size.)
  radius: { shell: 10, container: 8, pill: 8, badge: 3, button: 5, icon: 5, iconSm: 4, overlay: 10, sheet: 16 },
  // v4 component sizing (#1377, tokens/spacing.css + board): single source for the
  // shared primitives so a geometry change is one line here.
  size: {
    iconBox: 32, iconBoxCompact: 28, glyph: 17, glyphCompact: 15, iconBoxPill: 26,
    dot: 6, avatarBar: 24, avatarMenu: 34, avatarHero: 48,
    navIcon: 16, navPillW: 14, navPillH: 2,
    switchW: 30, switchH: 18,
    reconcileDesktop: 16, reconcileMobile: 20,
  },
  motion: {
    instant: '100ms', fast: '150ms', base: '200ms', slow: '300ms', shimmer: '1500ms',
    shimmerCycle: '12000ms',
    easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)',
    easeInOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
    easeSpring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
};

export const buildTheme = (isDarkMode) => {
  const mode = isDarkMode ? 'dark' : 'light';
  const t = TOKENS[mode];

  return createTheme({
    ds: DS,
    palette: {
      mode,
      // --interactive: links, active nav, focus ring (NOT button fill, see §2.6)
      primary: { main: t.interactive, contrastText: t.interactiveOn },
      secondary: { main: t.interactiveStrong, contrastText: t.interactiveOn },
      error: { main: t.negative },
      success: { main: t.positive },
      warning: {
        main: t.warning,
        // §8 deepened Unreconciled chip / DEV badge. Both modes now carry a filled
        // chip treatment: light = amber-800 on faint amber (#1119); dark = amber on
        // a deep amber-brown fill (#1245, v4 §3).
        deep: t.warningDeep,
        border: t.warningBorder,
        chipBg: t.warningChipBg,
      },
      info: { main: t.interactive },
      background: { default: t.surfacePage, paper: t.surfaceRaised },
      text: {
        primary: t.textPrimary,
        secondary: t.textMuted,
        disabled: t.textFaint,
        // extra v3 text roles (custom, read via theme.palette.text.*)
        default: t.textDefault,
        body: t.textBody,
        dim: t.textDim,
        faint: t.textFaint,
      },
      divider: t.borderHairline,
      // v4 (#1245/#1246): solid row-hover (spec --surface-2) in both themes so rows
      // lift off the visible card.
      action: { hover: t.surfaceHover },

      // ---- v3 custom palette slots (no MUI home) ----
      positiveStrong: t.positiveStrong,
      interactive: t.interactive,
      interactiveStrong: t.interactiveStrong,
      interactiveOn: t.interactiveOn,
      // v4 (#1245): emphasized border + FAB/avatar gradient end stop. Light (sage)
      // has no dedicated value yet, so fall back to in-palette tokens until #1246.
      borderStrong: t.borderStrong || t.borderHairline,
      accentDeep: t.accentDeep || t.interactiveStrong,
      warningSecondary: t.warningSecondary,
      buttonPrimary: { bg: t.buttonPrimaryBg, text: t.buttonPrimaryText },
      surface: {
        page: t.surfacePage, raised: t.surfaceRaised, bar: t.surfaceBar, overlay: t.surfaceOverlay,
        hover: t.surfaceHover,
        recessed: t.surfaceRecessed,
        // v4 (#1377): full-bleed hovered/active list row, between raised and hover
        // (spec --surface-row-hover). surface.hover stays the ACTIVE row + input fill.
        rowHover: t.surfaceRowHover,
      },
      // §7.1/§7.3 utility chrome foreground. v4 (#1377): both modes carry explicit
      // bar* tokens (tokens/colors-dark.css + colors-light.css); the dark bar text
      // and label drop to the muted/dim tiers per the v4 board (previously they
      // read textPrimary/textMuted).
      bar: { text: t.barText, label: t.barLabel, positive: t.barPositive, negative: t.barNegative, zero: t.barZero, navIdle: t.barNavIdle, navActive: t.barNavActive },
      cat: {
        checking: t.catChecking, credit: t.catCredit, savings: t.catSavings,
        budgets: t.catBudgets, income: t.catIncome, neutral: t.catNeutral,
      },
      tint: {
        checking: t.tintChecking, credit: t.tintCredit, savings: t.tintSavings,
        budgets: t.tintBudgets, income: t.tintIncome, warning: t.tintWarning,
        positive: t.tintPositive, negative: t.tintNegative, interactive: t.tintInteractive,
        neutral: t.tintNeutral,
      },
      // Search-match highlight (#1230): amber, mode-specific (dark = chip + ink,
      // light = translucent wash). Read via theme.palette.searchHighlight.{bg,text}.
      searchHighlight: { bg: t.searchHighlightBg, text: t.searchHighlightText },
      danger: { surface: t.surfaceDanger, border: t.borderDanger },
      // §2.7 floating overlays: menus, popovers, autocomplete dropdowns, the
      // bottom-nav slide-up drawer. Components read theme.palette.overlay.shadow.
      overlay: { shadow: t.overlayShadow, border: t.overlayBorder || t.borderStrong },
      // Entry Type colors (#1938, DESIGN_SYSTEM.md 8.15), derived from existing
      // tokens: Spending purple (interactive), Income gold (cat.income),
      // Transfer gray (text.secondary on a dim wash), One-Time blue
      // (cat.checking). `fg` is the letter and ring, `bg` the chip's tint.
      entryType: {
        spending: { fg: t.interactive, bg: alpha(t.interactive, 0.16) },
        income: { fg: t.catIncome, bg: alpha(t.catIncome, 0.14) },
        transfer: { fg: t.textMuted, bg: alpha(t.textDim, 0.22) },
        onetime: { fg: t.catChecking, bg: alpha(t.catChecking, 0.14) },
      },

      // Custom payday colors (unchanged; not part of v3 scope)
      payday: {
        main: isDarkMode ? '#2d5a87' : '#1976d2',
        light: isDarkMode ? '#1a3a5c' : '#42a5f5',
        dark: isDarkMode ? '#1e3d5f' : '#1565c0',
        contrastText: isDarkMode ? '#e3f2fd' : '#ffffff', // §2.8 exception: contrast text on saturated fill
      },
      // Guide popper colors (owned by guides/design.md, palette.tour; out of v3 scope)
      tour: {
        main: isDarkMode ? '#1565c0' : '#1976d2',
        contrastText: '#ffffff',
        border: isDarkMode ? '#1976d2' : '#0d47a1',
        tail: isDarkMode ? '#1565c0' : '#1976d2',
        tailBorder: isDarkMode ? '#1976d2' : '#0d47a1',
        buttonBg: '#ffffff',
        buttonBgHover: '#e3f2fd',
        buttonText: '#0d47a1',
        buttonNextBg: '#bbdefb',
        buttonNextText: '#0d47a1',
        focusRing: isDarkMode ? '#90caf9' : '#0d47a1',
        agendaBadge: '#0d47a1',
        agendaBadgeText: isDarkMode ? '#e3f2fd' : '#ffffff',
      },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontWeightRegular: 400,
      fontWeightMedium: 500,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { fontVariantNumeric: 'tabular-nums' },
          // #1221: native date-picker indicator follows the theme (light glyph in dark mode).
          'input[type="date"], input[type="time"], input[type="datetime-local"]': { colorScheme: isDarkMode ? 'dark' : 'light' },
          // §6.9 focus ring, never removed
          ':focus-visible': {
            outline: `1.5px solid ${t.interactive}`,
            outlineOffset: '2px',
            borderRadius: '4px',
          },
          // §6.8 button press
          button: { transition: `transform ${DS.motion.fast} ${DS.motion.easeOut}` },
          'button:active': { transform: 'scale(0.97)' },
          // §6.2 reduced motion
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
            },
          },
        },
      },
      // Quiet surfaces: keep exact paper color (no dark elevation tint).
      // Shadows left to component defaults so menus/dialogs still lift off the page.
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      // §"Hairlines not chrome": cards are flat with a 1px hairline border.
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: t.surfaceRaised,
            backgroundImage: 'none',
            boxShadow: 'none',
            border: `1px solid ${t.borderHairline}`,
            borderRadius: 8,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { textTransform: 'none', borderRadius: 5 },
          // Primary fill uses --button-primary-bg, never --interactive (§2.6)
          containedPrimary: {
            backgroundColor: t.buttonPrimaryBg,
            color: t.buttonPrimaryText,
            '&:hover': { backgroundColor: isDarkMode ? '#4338ca' : '#4338ca' },
          },
        },
      },
      // §3 FAB (#1245, v4): the primary floating action button is the one place a
      // gradient is allowed (spec §5 excepts FAB/avatar). 135deg accent -> accentDeep
      // with a soft accent-tinted drop. The `primary` slot targets color="primary"
      // FABs (the Ledgers + Budgets create buttons).
      MuiFab: {
        styleOverrides: {
          primary: {
            backgroundImage: `linear-gradient(135deg, ${t.interactive}, ${t.accentDeep || t.interactiveStrong})`,
            color: t.buttonPrimaryText,
            boxShadow: isDarkMode
              ? '0 6px 22px rgba(106, 92, 208, 0.50)'
              : '0 6px 18px rgba(15, 23, 42, 0.20)',
            '&:hover': {
              backgroundImage: `linear-gradient(135deg, ${t.interactive}, ${t.accentDeep || t.interactiveStrong})`,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: { root: { borderColor: t.borderHairline } },
      },
      // §8.7 switch (binary setting rows). MUI's default track paints pure
      // black/white alphas, a §2.8 violation, so both states get token fills:
      // checked = interactive track with interactiveOn thumb (saturated-fill
      // contrast exception), unchecked = borderStrong track with an overlay
      // thumb. Disabled states dim the same tokens rather than swapping hue.
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            color: t.surfaceOverlay,
            '&.Mui-checked': {
              color: t.interactiveOn,
              '& + .MuiSwitch-track': {
                backgroundColor: t.interactive,
                opacity: 1,
              },
              '&.Mui-disabled': {
                color: alpha(t.interactiveOn, 0.5),
                '& + .MuiSwitch-track': {
                  backgroundColor: alpha(t.interactive, 0.35),
                  opacity: 1,
                },
              },
            },
            '&.Mui-disabled': {
              color: alpha(t.surfaceOverlay, 0.6),
              '& + .MuiSwitch-track': {
                backgroundColor: alpha(t.borderStrong, 0.4),
                opacity: 1,
              },
            },
          },
          thumb: {
            backgroundColor: 'currentColor',
            boxShadow: 'none',
          },
          track: {
            backgroundColor: t.borderStrong,
            opacity: 1,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: t.borderHairline },
              '&:hover fieldset': { borderColor: t.textFaint },
            },
          },
        },
      },
      // §2.7 overlay surface: any floating layer (menus, popovers, autocomplete
      // dropdowns) must read as a distinct raised layer above the page. In dark
      // mode surface.page and surface.raised are nearly identical, so a shadow
      // alone is not enough separation. Three signals together:
      //   1. backgroundColor = surface.overlay (a step lighter than raised,
      //      added in #937) so the overlay does not paint on a same-colored
      //      row underneath.
      //   2. 1px overlay edge (overlayBorder, #1938): a neutral step above the surface.
      //   3. theme overlayShadow (strengthened in #937).
      // backgroundImage: 'none' defeats the global MuiPaper backdrop tint that
      // would otherwise darken the overlay back down toward surface.raised.
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: t.surfaceOverlay,
            backgroundImage: 'none',
            border: `1px solid ${t.overlayBorder}`,
            boxShadow: t.overlayShadow,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: t.surfaceOverlay,
            backgroundImage: 'none',
            border: `1px solid ${t.overlayBorder}`,
            boxShadow: t.overlayShadow,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: t.surfaceOverlay,
            backgroundImage: 'none',
            border: `1px solid ${t.overlayBorder}`,
            boxShadow: t.overlayShadow,
          },
        },
      },
    },
  });
};

export const CustomThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Default to dark (v3); existing users keep their stored preference.
    const saved = localStorage.getItem('darkMode');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const toggleDarkMode = () => {
    setIsDarkMode(prev => {
      const newValue = !prev;
      localStorage.setItem('darkMode', JSON.stringify(newValue));
      return newValue;
    });
  };

  const theme = buildTheme(isDarkMode);

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
